import { Link, Outlet, useLocation } from "react-router-dom"
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  BarChart3,
  Users,
  Trophy,
  LogOut,
  Building2,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/contexts/auth-context"
import { useLogout } from "@/hooks/use-auth"
import { useTranslation } from "react-i18next"
import { useAuthenticatedMatches } from "@/hooks/use-authenticated-matches"
import { ThemeToggle } from "@/components/ui/ThemeToggle"
import { UserCard } from "@/components/UserCard"
import { cn } from "@/lib/utils"

function DashboardContent() {
  const location = useLocation()
  const { user } = useAuth()
  const { mutate: logout } = useLogout()
  const { t } = useTranslation('admin')
  const { setOpenMobile, isMobile } = useSidebar()
  useAuthenticatedMatches();

  const handleLogout = () => {
    logout()
  }

  // Function to handle navigation and close sidebar on mobile
  const handleNavigation = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  const dashboardIsActive = location.pathname === "/dashboard";
  const usersIsActive = location.pathname.startsWith("/users");
  const matchesIsActive = location.pathname.startsWith("/matches");
  const organizationsIsActive = location.pathname.startsWith("/organizations");

  return (
    <>
      <Sidebar className="border-r">
        <SidebarHeader className="border-b px-6 py-4">
          <div className="flex items-center gap-3">
            <img src="/favicon/android-chrome-192x192.png" alt="Padlo Coach" className="h-10 w-10" />
            <span className="font-semibold text-lg text-foreground">{t('navigation.appName')}</span>
          </div>
        </SidebarHeader>
        <SidebarContent className="px-3 py-6">
          <SidebarMenu className="flex flex-col gap-1">
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={dashboardIsActive}
                className={cn("px-3 py-2.5 text-base font-medium group/navitem", { "shadow-md shadow-primary/30": dashboardIsActive })}
              >
                <Link to="/dashboard" onClick={handleNavigation} className="flex items-center gap-3">
                  <BarChart3 className={cn("h-5 w-5 transition-all duration-300 ease-in-out group-hover/navitem:text-primary group-hover/navitem:-translate-y-0.5 group-hover/navitem:rotate-2", { "text-primary": dashboardIsActive })} />
                  <span className="text-current">{t('navigation.dashboard')}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>

            <div className="mt-4">
              {user?.coach_viewer === false && (
                <div className="px-3 mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  {t('navigation.athletesManagement')}
                </div>
              )}
              <div className="flex flex-col gap-1">
                {user?.coach_viewer === false && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={usersIsActive}
                      className={cn("px-3 py-2.5 text-base font-medium group/navitem", { "shadow-md shadow-primary/30": usersIsActive })}
                    >
                      <Link to="/users" onClick={handleNavigation} className="flex items-center gap-3">
                        <Users className={cn("h-5 w-5 transition-all duration-300 ease-in-out group-hover/navitem:text-primary group-hover/navitem:-translate-y-0.5 group-hover/navitem:rotate-2", { "text-primary": usersIsActive })} />
                        <span className="text-current">{t('navigation.myAthletes')}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}

                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={matchesIsActive}
                    className={cn("px-3 py-2.5 text-base font-medium group/navitem", { "shadow-md shadow-primary/30": matchesIsActive })}
                  >
                    <Link to="/matches" onClick={handleNavigation} className="flex items-center gap-3">
                      <Trophy className={cn("h-5 w-5 transition-all duration-300 ease-in-out group-hover/navitem:text-primary group-hover/navitem:-translate-y-0.5 group-hover/navitem:rotate-2", { "text-primary": matchesIsActive })} />
                      <span className="text-current">{t('navigation.matches')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </div>
            </div>

            {user?.coach_viewer === false && (
              <div className="mt-4">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={organizationsIsActive}
                    className={cn("px-3 py-2.5 text-base font-medium group/navitem", { "shadow-md shadow-primary/30": organizationsIsActive })}
                  >
                    <Link to="/organizations" onClick={handleNavigation} className="flex items-center gap-3">
                      <Building2 className={cn("h-5 w-5 transition-all duration-300 ease-in-out group-hover/navitem:text-primary group-hover/navitem:-translate-y-0.5 group-hover/navitem:rotate-2", { "text-primary": organizationsIsActive })} />
                      <span className="text-current">{t('navigation.organizations')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </div>
            )}
          </SidebarMenu>
        </SidebarContent>
        <SidebarFooter className="border-t p-6">
          <div className="mb-4 flex justify-center">
            <ThemeToggle />
          </div>
          <UserCard className="mb-4" />
          <Button variant="outline" className="w-full gap-2 border-border text-foreground hover:bg-muted" onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
            {t('navigation.signOut')}
          </Button>
        </SidebarFooter>
      </Sidebar>
      <main className="min-h-screen flex-1 px-8 py-8 overflow-x-hidden bg-background">
        <SidebarTrigger className="mb-6" />
        <div className="container max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>
    </>
  )
}

export function DashboardLayout() {
  return (
    <SidebarProvider>
      <DashboardContent />
    </SidebarProvider>
  )
}
